"use client"

import React from "react"
import { useRouter } from "next/navigation"

import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import { applyUserTranslationsAllTypes } from "@/lib/utils"
import type { Page, SubPage } from "@/types/models/menu"

const activeClass = "bg-sidebar-accent text-sidebar-accent-foreground"
const hoverClass = "hover:bg-sidebar-accent/60"
const federBase = "/proyectos-financiados/feder-pai"

const normalizePath = (path: string) => path.replace(/\/+/g, "/").replace(/\/$/, "")

const getFederScopedSlug = (baseSlug: string, paiId: string, subpageSlug: string) => {
  const slug = normalizePath(subpageSlug).replace(/^\//, "")

  // Contracts and transactions share the execution view inside a selected PAI.
  const scopedSection =
    slug === "contratos" || slug === "transacciones" ? "ejecucion" : slug

  return normalizePath(
    scopedSection ? `${baseSlug}/${paiId}/${scopedSection}` : `${baseSlug}/${paiId}`
  )
}

type AppSidebarSubPageItemProps = {
  subpage: SubPage
  baseSlug: string
  isFederPage: boolean
  normalizedPathname: string
  numericPaiId?: string
  userLang?: string
}

export const AppSidebarSubPageItem: React.FC<AppSidebarSubPageItemProps> = ({
  subpage,
  baseSlug,
  isFederPage,
  normalizedPathname,
  numericPaiId,
  userLang,
}) => {
  const router = useRouter()
  const isRootSubpage = !subpage.slug || subpage.slug === "/" || subpage.slug === "index"

  const finalSlug = isFederPage && numericPaiId
    ? getFederScopedSlug(baseSlug, numericPaiId, isRootSubpage ? "" : subpage.slug)
    : subpage.slug && subpage.slug !== "/"
      ? normalizePath(`${baseSlug}/${subpage.slug}`)
      : baseSlug

  const isSubmenuActive = isRootSubpage
    ? normalizedPathname === finalSlug
    : normalizedPathname === finalSlug || normalizedPathname.startsWith(`${finalSlug}/`)

  const label = applyUserTranslationsAllTypes([subpage], userLang ?? "", ["text"])[0].text

  return (
    <SidebarMenuItem key={finalSlug}>
      <SidebarMenuButton
        onClick={() => router.push(finalSlug)}
        className={isSubmenuActive ? activeClass : hoverClass}
      >
        <span className="text-sm">{label}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

type AppSidebarSubPagesProps = {
  page: Page
  subpages: SubPage[]
  isDipu: boolean
  isInFederDetail: boolean
  normalizedPathname: string
  numericPaiId?: string
  userLang?: string
}

export const AppSidebarSubPages: React.FC<AppSidebarSubPagesProps> = ({
  page,
  subpages,
  isDipu,
  isInFederDetail,
  normalizedPathname,
  numericPaiId,
  userLang,
}) => {
  if (!subpages?.length) return null

  const baseSlug = normalizePath(page.slug)
  const isFederPage = baseSlug.includes(federBase)

  if (isFederPage && !isDipu && !isInFederDetail) return null
  if (!normalizedPathname.startsWith(baseSlug)) return null

  const visibleSubpages =
    isFederPage && isDipu && !isInFederDetail
      ? subpages.flatMap((subpage) => {
          const slug = normalizePath(subpage.slug ?? "").replace(/^\//, "")

          if (slug === "indicadores") return []
          if (slug !== "ejecucion") return [subpage]

          return [
            { ...subpage, slug: "contratos", text: "Contratos", translations: [] },
            { ...subpage, slug: "transacciones", text: "Transacciones", translations: [] },
          ]
        })
      : subpages

  return (
    <div className="mb-1 ml-8 mt-1">
      <SidebarMenu>
        {visibleSubpages.map((subpage) => (
          <AppSidebarSubPageItem
            key={`${page.slug}:${subpage.slug ?? "index"}`}
            subpage={subpage}
            baseSlug={baseSlug}
            isFederPage={isFederPage}
            normalizedPathname={normalizedPathname}
            numericPaiId={numericPaiId}
            userLang={userLang}
          />
        ))}
      </SidebarMenu>
    </div>
  )
}
